<script lang="ts">
  import { getStoresContext, type StoresKey } from "~/stores";
  import Swiper from 'swiper';
  import { Navigation, Pagination, Autoplay } from 'swiper/modules';
  import { onMount } from 'svelte';

  export let slides: {
    image: {
      desktopSrc: string;
      mobileSrc: string;
      alt: string;
    };
    title: string;
    subtitle: string;
    cta: {
      text: string;
      href: string;
    };
  }[];
  export let request_store_key: StoresKey;
  export let hideMainBannerText: boolean = true;
  export let borderRadius = "16px";
  export let enableBannerHoverEffect: boolean = false;
  export let enableAutoplayAndLoop: boolean = true;
  
  // Pagination styling props
  export let paginationActiveBulletColor = "#FFFFFF";
  export let paginationInactiveBulletColor = "rgba(255, 255, 255, 0.5)";
  export let paginationActiveBulletWidth = "12px";
  export let paginationActiveBulletHeight = "8px";
  export let paginationInactiveBulletWidth = "8px";
  export let paginationInactiveBulletHeight = "8px";
  export let paginationBorderRadius = "0";
  
  let _class;
  export { _class as class };

  const { locale, T } = getStoresContext(request_store_key);

  $: t = $T;
  $: isSingleSlide = slides?.length === 1;
  $: singleSlide = isSingleSlide ? slides[0] : null;

  let swiperEl: HTMLElement;
  let swiperInstance: Swiper | null = null;
  let isClient = false;
  let isBeginning = false;
  let isEnd = false;
  let isMobile = false;

  onMount(() => {
    isClient = true;
    isMobile = window.innerWidth <= 768;
    return () => {
      if (swiperInstance) {
        swiperInstance.destroy(true, true);
        swiperInstance = null;
      }
    };
  });

  $: if (isClient && swiperEl && !swiperInstance && !isSingleSlide) {
    setTimeout(() => {
      const swiperConfig: any = {
        modules: [Navigation, Pagination],
        navigation: {
          prevEl: '.main-banner__button-prev',
          nextEl: '.main-banner__button-next'
        },
        pagination: {
          el: '.main-banner__pagination',
          clickable: true
        },
        loop: enableAutoplayAndLoop,
        observer: true,
        observeParents: true
      };

      if (enableAutoplayAndLoop) {
        swiperConfig.modules.push(Autoplay);
        swiperConfig.autoplay = {
          delay: 5000,
          disableOnInteraction: false
        };
      }

      swiperInstance = new Swiper(swiperEl, swiperConfig);
    }, 0);
  }

  $: if (swiperInstance && slides && !isSingleSlide) {
    swiperInstance.update();
  }

</script>

{#if $locale}
  <div class="main-banner {_class}" 
    style="
      --banner-pagination-active-bullet-color: {paginationActiveBulletColor};
      --banner-pagination-inactive-bullet-color: {paginationInactiveBulletColor};
      --banner-pagination-active-bullet-width: {paginationActiveBulletWidth};
      --banner-pagination-active-bullet-height: {paginationActiveBulletHeight};
      --banner-pagination-inactive-bullet-width: {paginationInactiveBulletWidth};
      --banner-pagination-inactive-bullet-height: {paginationInactiveBulletHeight};
      --banner-pagination-border-radius: {paginationBorderRadius};
    ">
    
    {#if isSingleSlide && singleSlide}
      <!-- Single slide - no Swiper needed -->
      <div class="main-banner__single-slide" style="border-radius: {borderRadius}">
        {#if singleSlide.cta?.href && singleSlide.cta.href.trim() !== ''}
          <a href={singleSlide.cta.href} class="banner-link{enableBannerHoverEffect ? ' hover-enabled' : ''}">
            <div class="banner-image">
              <img 
                src={isMobile ? singleSlide.image.mobileSrc : singleSlide.image.desktopSrc}
                alt={singleSlide.image.alt}
              />
            </div>
            <div class="banner-content">
              <div class="{hideMainBannerText ? 'hideMainBannerText' : ''}">
                {#if singleSlide.subtitle}
                <p class="banner-subtitle">{singleSlide.subtitle}</p>
                {/if}
                {#if singleSlide.title}
                 <h2 class="banner-title">{singleSlide.title}</h2>
                {/if}
              </div>
            </div>
          </a>
        {:else}
          <div class="banner-image">
            <img 
              src={isMobile ? singleSlide.image.mobileSrc : singleSlide.image.desktopSrc}
              alt={singleSlide.image.alt}
            />
          </div>
          <div class="banner-content">
            <div class="{hideMainBannerText ? 'hideMainBannerText' : ''}">
              {#if singleSlide.subtitle}
              <p class="banner-subtitle">{singleSlide.subtitle}</p>
              {/if}
              {#if singleSlide.title}
               <h2 class="banner-title">{singleSlide.title}</h2>
              {/if}
            </div>
          </div>
        {/if}
      </div>
    {:else}
      <!-- Multiple slides - use Swiper -->
      <div class="main-banner__swiper swiper" style="border-radius: {borderRadius}" bind:this={swiperEl}>
        <div class="swiper-wrapper">
          {#each slides as slide, i (i)}
            <div class="swiper-slide">
              {#if slide.cta?.href && slide.cta.href.trim() !== ''}
                <a href={slide.cta.href} class="banner-link{enableBannerHoverEffect ? ' hover-enabled' : ''}">
                  <div class="banner-image">
                    <img 
                      src={isMobile ? slide.image.mobileSrc : slide.image.desktopSrc}
                      alt={slide.image.alt}
                      on:load={() => {
                        if (swiperInstance) swiperInstance.update();
                      }}
                    />
                  </div>
                  <div class="banner-content">
                    <div class="{hideMainBannerText ? 'hideMainBannerText' : ''}">
                      {#if slide.subtitle}
                      <p class="banner-subtitle">{slide.subtitle}</p>
                      {/if}
                      {#if slide.title}
                       <h2 class="banner-title">{slide.title}</h2>
                      {/if}
                    </div>
                  </div>
                </a>
              {:else}
                <div class="banner-image">
                  <img 
                    src={isMobile ? slide.image.mobileSrc : slide.image.desktopSrc}
                    alt={slide.image.alt}
                    on:load={() => {
                      if (swiperInstance) swiperInstance.update();
                    }}
                  />
                </div>
                <div class="banner-content">
                  <div class="{hideMainBannerText ? 'hideMainBannerText' : ''}">
                    {#if slide.subtitle}
                    <p class="banner-subtitle">{slide.subtitle}</p>
                    {/if}
                    {#if slide.title}
                     <h2 class="banner-title">{slide.title}</h2>
                    {/if}
                  </div>
                </div>
              {/if}
            </div>
          {/each}
        </div>
        
        {#if slides.length > 1}
          <button 
            class="main-banner__button-prev" 
            class:swiper-button-disabled={isBeginning}
            aria-label="Previous slide"
            on:click={() => swiperInstance?.slidePrev()}
          >
            <div class="chevron-icon chevron-left"></div>
          </button>
          <button 
            class="main-banner__button-next"
            class:swiper-button-disabled={isEnd}
            aria-label="Next slide"
            on:click={() => swiperInstance?.slideNext()}
          >
            <div class="chevron-icon chevron-right"></div>
          </button>
          <div class="main-banner__pagination"></div>
        {/if}
      </div>
    {/if}
  </div>
{/if}

<style lang="scss">
  .main-banner {
    margin: 0 auto;
    position: relative;
    width: 100%;
    background-color: transparent;
    padding: 0;
    
    &__single-slide {
      width: 100%;
      position: relative;
      overflow: hidden;
    }
    
    &__swiper {
      width: 100%;
      position: relative;
    }

    // Navigation buttons
    &__button-prev,
    &__button-next {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 40px;
      height: 40px;
      z-index: 10;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.3);
      border: none;
      border-radius: 50%;
      color: #FFFFFF;
      backdrop-filter: blur(4px);
      transition: background-color 0.3s ease;
      -webkit-tap-highlight-color: transparent;
      -webkit-appearance: none;
      padding: 0;

      @media (max-width: 768px) {
        top: auto;
        bottom: 28px;
        transform: none;
        width: 32px;
        height: 32px;
      }

      .chevron-icon {
        width: 8px;
        height: 8px;
        border-style: solid;
        border-width: 2px 2px 0 0;
        border-color: #FFFFFF;
        position: relative;
        display: block;
      }
      
      .chevron-left {
        transform: rotate(-135deg);
        margin-left: 2px;
      }
      
      .chevron-right {
        transform: rotate(45deg);
        margin-right: 2px;
      }

      &:hover {
        background: rgba(0, 0, 0, 0.5);
      }

      &:global(.swiper-button-disabled) {
        opacity: 0.35;
        cursor: not-allowed;
      }
    }

    &__button-prev {
      left: 20px;
    }

    &__button-next {
      right: 20px;
    }

    // Pagination
    &__pagination {
      position: absolute;
      bottom: 40px;
      top: auto;
      display: flex;
      gap: 12px;
      z-index: 10;
      justify-content: center;
      align-items: center;
      width: auto;
      left: 50%;
      transform: translateX(-50%);

      :global(.swiper-pagination-bullet) {
        width: var(--banner-pagination-inactive-bullet-width, 8px) !important;
        height: var(--banner-pagination-inactive-bullet-height, 8px) !important;
        background: var(--banner-pagination-inactive-bullet-color, rgba(255, 255, 255, 0.5)) !important;
        opacity: 1;
        margin: 0;
        border-radius: var(--banner-pagination-border-radius, 0);
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          opacity: 0.8;
        }
      }

      :global(.swiper-pagination-bullet-active) {
        opacity: 1;
        width: var(--banner-pagination-active-bullet-width, 12px) !important;
        height: var(--banner-pagination-active-bullet-height, 12px) !important;
        background: var(--banner-pagination-active-bullet-color, #FFFFFF) !important;
      }
    }

    .banner-image {
      position: relative;
      width: 100%;

      img {
        width: 100%;
        height: auto;
        display: block;
      }
    }

    .banner-content {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      z-index: 2;
      width: 90%;
      max-width: 800px;
    }

    .hideMainBannerText{
      display: none;
    }

    .banner-title {
      --base-size: 3.5rem;
      font-size: var(--base-size);
      font-weight: 700;
      margin: 1rem 0;
      line-height: 1.2;

      @media (max-width: 768px) {
        font-size: calc(var(--base-size) * 0.8);
      }
    }

    .banner-subtitle {
      --base-size: 1.5rem;
      font-size: var(--base-size);
      margin-bottom: 1rem;
      
      @media (max-width: 768px) {
        font-size: calc(var(--base-size) * 0.8);
      }
    }

    .banner-link {
      display: block;
      position: relative;
      width: 100%;
      height: 100%;
      text-decoration: none;
      color: inherit;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
    }

    .banner-link.hover-enabled {
      transition: transform 0.3s ease;
    }
    .banner-link.hover-enabled:hover {
      transform: scale(1.02);
    }
    .banner-link.hover-enabled:focus {
      outline: 2px solid rgba(255, 255, 255, 0.5);
      outline-offset: 2px;
    }
  }
</style> 